// Mirror of backend Pydantic schemas (src/recognition/api/schemas.py).
// Hand-curated for U0..U3; once an OpenAPI spec is published (backend M1)
// this file is replaced by `openapi-typescript`-generated output.

export type UUID = string;
export type ISODateTime = string;

export type Role = "admin" | "manager" | "user";

export type MeetingStatus =
  | "pending"
  | "processing"
  | "done"
  | "partial"
  | "error";

export type AnalysisStatus = "processing" | "partial" | "done" | "error";

/** LLM model allow-list (mirror of `LLMModelName` Literal on backend). */
export const LLM_MODEL_NAMES = [
  "claude_opus",
  "openai_gpt",
  "deepseek",
] as const;
export type LLMModelName = (typeof LLM_MODEL_NAMES)[number];

/** API-key provider allow-list. */
export const API_KEY_PROVIDERS = [
  "claude",
  "openai",
  "deepseek",
  "assemblyai",
] as const;
export type ApiKeyProvider = (typeof API_KEY_PROVIDERS)[number];

/** Custom-field types — mirror of backend Literal. */
export const CUSTOM_FIELD_TYPES = [
  "text",
  "number",
  "date",
  "dropdown",
  "boolean",
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export type CustomFieldEntityType = "client" | "employee";

export interface UserResponse {
  id: UUID;
  email: string;
  role: Role;
  employee_id: UUID | null;
  is_active: boolean;
}

export interface MeetingPrecheckRequest {
  sha256: string;
}

export interface MeetingPrecheckResponse {
  exists: boolean;
  meeting_id?: UUID | null;
}

export interface MeetingSummary {
  id: UUID;
  /**
   * Sequential «№ встречи» (T1 / FR-26). Independent of `id`; assigned by
   * Postgres IDENTITY on insert, never reused, never affected by filter/sort.
   */
  meeting_no: number;
  original_filename: string;
  status: MeetingStatus;
  meeting_type_id: UUID;
  client_id: UUID;
  uploaded_by: UUID;
  /**
   * Pre-resolved «Кто загрузил»: Employee.full_name for users linked to an
   * Employee, otherwise the user's email (admin bootstrap accounts). Null
   * only when the backend couldn't resolve the row at all.
   */
  uploader_display: string | null;
  /**
   * Employee.full_name list for each entry in `meeting.participants[]`,
   * preserving order. Unknown participant IDs are skipped silently rather
   * than failing the row.
   */
  participants_display: string[];
  created_at: ISODateTime;
  /**
   * Owning folder (papki-tenants v1). Optional because legacy rows minted
   * before the folder migration carry no value and the FE renders defensively.
   * Used by the registry to render a per-row folder badge for multi-folder
   * users; folder-scoped filtering is enforced server-side via the
   * `X-Active-Folder` request header.
   */
  folder_id?: UUID;
}

// ---- Folders (papki-tenants v1) -----------------------------------------

/**
 * A user-scoped organizational namespace ("папка"). A user can belong to N
 * folders; at any moment exactly one folder is "active" and scopes every
 * server-side query that supports the `X-Active-Folder` header.
 *
 * The active folder is stored server-side as part of the session so it
 * survives refresh; the FE mirrors it in a Zustand slice to keep the
 * dropdown render synchronous (no flash of empty UI on mount).
 */
export interface Folder {
  id: UUID;
  name: string;
  is_default: boolean;
  // ``is_active`` и ``created_at`` отсюда убраны 24.10.2026: сервер их не
  // отдаёт (FolderResponse — это id, name, is_default, store_video), а
  // ``is_active`` вообще не существует ни в модели, ни в таблице. Тип
  // обещал обязательные поля, которых нет: первый, кто написал бы
  // ``folder.created_at``, получил бы undefined при зелёном tsc.
  // ``description`` оставлен намеренно — на нём держится колонка в таблице
  // и поле в форме создания, а вопрос «нужны ли папкам описания» открыт.
  /**
   * Хранить ли видеофайлы встреч этой папки. При `false` сервер после
   * успешного распознавания заменяет видео извлечённым звуком и удаляет
   * видеофайл — экономия до 95% места. По умолчанию `true`.
   */
  store_video: boolean;
}

export interface FolderCreate {
  name: string;
}

/** Тело PATCH /api/folders/{id} — пока меняется только хранение видео. */
export interface FolderUpdate {
  store_video: boolean;
}

export interface ActiveFolderResponse {
  folder_id: UUID | null;
}

/**
 * Folder membership types (Variant A, 2026-05-22).
 *
 * The per-folder role concept was retired together with migration 0109 —
 * the `folder_members.role` column is gone, and so is the
 * `FolderMemberRole` TS alias. Composition is flat now: a user is either
 * in a folder or not. Mutations are gated on the global `users.role`
 * (admin only) at the BE.
 */
export interface FolderMember {
  folder_id: UUID;
  user_id: UUID;
}

export interface FolderMemberCreate {
  user_id: UUID;
}

/**
 * Quote-with-timecode evidence for an artifact (v1.1.4 feature). Backend
 * attaches `citations[]` to artifacts whose claims trace back to specific
 * utterances in the transcript so operators can verify LLM output.
 *
 *  - `quote` — verbatim text from the utterance (or aggregated paraphrase
 *    when `evidence_type === "aggregated"`).
 *  - `start_ms` / `end_ms` — utterance window in milliseconds; `null` when
 *    the citation is aggregated across multiple utterances.
 *  - `verified` — `true` when the backend re-found the quote in the
 *    transcript token-stream, `false` when the quote is plausible but the
 *    exact match could not be located (LLM hallucination guard).
 */
export interface Citation {
  quote: string;
  start_ms?: number | null;
  end_ms?: number | null;
  evidence_type?: "direct" | "aggregated";
  verified?: boolean;
}

export interface ArtifactText {
  title: string;
  type: "text";
  body: string;
  citations?: Citation[];
}

export interface ArtifactList {
  title: string;
  type: "list";
  items: string[];
  citations?: Citation[];
}

export interface ArtifactTable {
  title: string;
  type: "table";
  columns: string[];
  rows: string[][];
  citations?: Citation[];
}

export type Artifact = ArtifactText | ArtifactList | ArtifactTable;

export interface AnalysisVersionResponse {
  id: UUID;
  version: number;
  status: AnalysisStatus;
  snapshot_models: string[];
  snapshot_consensus_model: string | null;
  artifacts: Artifact[];
  error_phase: number | null;
  error_step: string | null;
  error_provider: string | null;
  created_at: ISODateTime;
  /**
   * Linked PromptVersion identity + content, promoted onto every version row
   * by the meeting-card loader so the Versions tab can render an in-place
   * disclosure of the exact prompt that produced this run (TaskMe ab1babf1).
   *
   * See `docs/api/show-prompt-version.contract.md`. All fields are required:
   * the FK on `analysis_versions.prompt_version_id` is `NOT NULL`, so the
   * backend cannot serialise a row with a missing prompt; the FE treats a
   * missing field as a server-side bug, not as a render branch.
   */
  prompt_version_id: UUID;
  prompt_version_number: number;
  prompt_text: string;
  prompt_meeting_type_name: string;
  prompt_created_at: ISODateTime;
  prompt_is_active: boolean;
  /**
   * TM-fe04aa2b — `true` when this run was launched with a one-off
   * operator-typed prompt (override on `/rerun`). The Versions tab
   * surfaces a «свой промпт» badge. Optional in the wire shape so
   * pre-migration snapshots deserialise; runtime BE always emits a bool.
   */
  is_custom_prompt?: boolean;
}

/** Body of `POST /api/meetings/:id/rerun` (TM-fe04aa2b, optional). */
export interface MeetingRerunRequest {
  /**
   * One-off prompt for this run only. Empty / omitted ⇒ active prompt
   * of the meeting type (legacy behaviour). Capped at 50000 chars by
   * the backend.
   */
  custom_prompt_text?: string | null;
}

export interface TranscriptUtterance {
  /** AssemblyAI diarization label: "A", "B", "C", ... */
  speaker: string;
  text: string;
  start_ms: number;
  end_ms: number;
}

export interface TranscriptResponse {
  id: UUID;
  language: string;
  text: string;
  /** Empty for text input and pre-v1.1.3 transcripts (no diarization). */
  utterances: TranscriptUtterance[];
  created_at: ISODateTime;
}

export interface MeetingCard {
  id: UUID;
  /**
   * Sequential «№ встречи» — same value as `MeetingSummary.meeting_no`
   * (T1 / FR-26). Rendered in the card header next to the filename so the
   * operator can correlate the open card with the registry row.
   */
  meeting_no: number;
  original_filename: string;
  download_url: string;
  status: MeetingStatus;
  meeting_type_id: UUID;
  client_id: UUID;
  participants: UUID[];
  uploaded_by: UUID;
  created_at: ISODateTime;
  transcript: TranscriptResponse | null;
  versions: AnalysisVersionResponse[];
  /**
   * Number of analysis runs for this meeting (TaskMe 8760a3e1). Counts the
   * initial run, so a fresh upload reads `1`. Surfaced in the card header
   * as «N запусков». Optional: legacy responses written before the
   * backend change carry no value and the FE renders defensively.
   */
  rerun_count?: number;
  /**
   * Sum of `pipeline_events.payload.cost_usd` across every version
   * (TaskMe 8760a3e1). Backend serialises a Decimal — typically as a
   * string (Pydantic default for Decimal); the FE coerces via
   * `Number(value).toFixed(2)` before display. Accept both shapes so a
   * future server-side serialiser flip-flop doesn't break the UI.
   */
  total_cost_usd?: string | number;
  /**
   * Owning folder (folder-management-v3 §3.2). Returned by the move endpoint
   * so the FE can show the new folder in the «Папка» field without a refetch.
   * Optional because legacy data may carry no value.
   */
  folder_id?: UUID | null;
}

// ---- Chat (TM-d6d6bf78 + TM-2f2aa823) ------------------------------------

/** Wire shape of a single chat message under a meeting card. */
export type ChatMessageRole = "user" | "assistant" | "system";

export interface MeetingMessage {
  id: UUID;
  meeting_id: UUID;
  /** `null` ⇒ assistant (LLM) or system message. */
  author_id: UUID | null;
  /**
   * Resolved on read by the BE: `employees.full_name` if the user has a
   * linked employee row, else `users.email`. `null` for assistant/system
   * messages and for orphaned author_id (soft-deleted user).
   */
  author_name: string | null;
  role: ChatMessageRole;
  text: string;
  /**
   * Assistant: `{model, input_tokens, output_tokens, cost_usd,
   * cache_hit_pct, context_mode}`. User: usually `null` or
   * `{context_mode: 'v2'}` when a slash command flipped it.
   */
  llm_meta: Record<string, unknown> | null;
  created_at: ISODateTime;
}

/** One row in `GET /api/meetings/:id/timeline` (T2 / 5-step indicator). */
export type TimelineStatus = "start" | "done" | "error" | "skipped";

export interface TimelineEvent {
  /** 1 = ingest, 2 = route, 3 = transcribe, 4 = llm, 5 = consensus. */
  phase: 1 | 2 | 3 | 4 | 5;
  step: string;
  status: TimelineStatus;
  attempt: number;
  provider: string | null;
  message: string | null;
  created_at: ISODateTime;
}

export interface MeetingTimelineResponse {
  meeting_id: UUID;
  events: TimelineEvent[];
}

/** Backend `RecognitionError` envelope (see core.errors / api routers). */
export interface RecognitionErrorEnvelope {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/** Minimal meeting-type DTO for the upload form select. */
export interface MeetingTypeSummary {
  id: UUID;
  name: string;
}

export interface ClientSummary {
  id: UUID;
  name: string;
  aliases?: string[];
}

export interface EmployeeSummary {
  id: UUID;
  full_name: string;
  aliases?: string[];
}

/** Subset of system_setting needed by the UI upload validators. */
export interface SystemSettings {
  upload_max_bytes: number;
  upload_allowed_extensions: string[];
}

// ----- Full Client / Employee / MeetingType DTOs (U3) ----------------------

export interface ClientResponse {
  id: UUID;
  name: string;
  inn: string | null;
  contacts: Record<string, unknown>;
  description: string | null;
  aliases: string[];
  custom_field_values: Record<string, unknown>;
  /**
   * Owning folder (papki-tenants v1). Null for clients carried over from
   * the pre-folder data model; backend writes the active folder on every
   * new client. Read-only from the FE — folder changes happen via the
   * dedicated "move to folder" admin flow, not via the client PATCH.
   */
  folder_id?: UUID | null;
  created_at: ISODateTime;
}

export interface ClientCreate {
  name: string;
  inn?: string | null;
  contacts?: Record<string, unknown>;
  description?: string | null;
  aliases?: string[];
  custom_field_values?: Record<string, unknown>;
  /**
   * Owning folder (folder-management-v3 §2.1). Required for non-admin
   * callers; the FE always sends it from the picker. Optional in the
   * type to keep back-compat with admin callers that omit it.
   */
  folder_id?: UUID | null;
}

export interface ClientUpdate {
  name?: string;
  inn?: string | null;
  contacts?: Record<string, unknown> | null;
  description?: string | null;
  aliases?: string[] | null;
  custom_field_values?: Record<string, unknown> | null;
}

/**
 * Slim view of the User that is linked to an Employee, as embedded inside
 * `EmployeeResponse.linked_user` (TM-5d6213f1). One employee → at most one
 * user (FK `users.employee_id`); the field is `null` when no account exists
 * for that employee yet.
 */
export interface LinkedUser {
  id: UUID;
  email: string;
  role: Role;
  is_active: boolean;
}

export interface EmployeeResponse {
  id: UUID;
  full_name: string;
  position: string | null;
  department: string | null;
  aliases: string[];
  custom_field_values: Record<string, unknown>;
  /**
   * All folders the employee belongs to (TM-5d6213f1). The previous
   * single-folder model (`folder_id`) was retired together with the
   * `POST /api/employees/{id}/move` endpoint — folder membership is now
   * mutated via PATCH `folder_ids` (atomic replace). Order is not
   * guaranteed.
   */
  folder_ids: UUID[];
  /** Soft-delete flag (TM-5d6213f1). Hard delete is forbidden by ADR-011. */
  is_active: boolean;
  /** Linked user account, when one exists. */
  linked_user: LinkedUser | null;
  /**
   * Count of meetings where this employee appears in `meetings.participants`
   * (TM-c4b88129). Server-computed via a single aggregate SELECT over the
   * full result set — no extra round-trip per row.
   */
  meeting_count: number;
  /**
   * Count of `manager_analyses` rows for this employee, excluding
   * `status = 'error'` (TM-c4b88129, ADR-019). 0 when the
   * `FEATURE_MANAGER_ANALYSIS` flag is off — the table is empty in that
   * case.
   */
  analysis_count: number;
  created_at: ISODateTime;
}

export interface EmployeeCreate {
  full_name: string;
  position?: string | null;
  department?: string | null;
  aliases?: string[];
  custom_field_values?: Record<string, unknown>;
  /**
   * Folders the new employee belongs to (TM-5d6213f1). Minimum one folder;
   * the BE rejects an empty list with 422. Replaces the legacy single
   * `folder_id` field.
   */
  folder_ids: UUID[];
}

export interface EmployeeUpdate {
  full_name?: string;
  position?: string | null;
  department?: string | null;
  aliases?: string[] | null;
  custom_field_values?: Record<string, unknown> | null;
  /**
   * Atomic replace of folder memberships (TM-5d6213f1). `undefined` →
   * memberships untouched; a list → BE replaces the membership rows in one
   * transaction. Empty list is invalid (BE → 422).
   */
  folder_ids?: UUID[];
}

export interface MeetingTypeResponse {
  id: UUID;
  name: string;
  description: string | null;
  llm_models: string[];
  consensus_model: string | null;
  is_active: boolean;
  created_at: ISODateTime;
  /** Per-type chat system prompt override (TM-d6d6bf78). `null` ⇒ default. */
  chat_system_prompt?: string | null;
}

export interface MeetingTypeCreate {
  name: string;
  description?: string | null;
  llm_models: LLMModelName[];
  consensus_model?: LLMModelName | null;
  /** Per-type chat system prompt override (TM-d6d6bf78). */
  chat_system_prompt?: string | null;
}

export interface MeetingTypeUpdate {
  name?: string;
  description?: string | null;
  llm_models?: LLMModelName[];
  consensus_model?: LLMModelName | null;
  is_active?: boolean;
  /** Explicit `null` clears the field; omission leaves it alone (TM-d6d6bf78). */
  chat_system_prompt?: string | null;
}

// ----- Prompt versions, API keys, settings, errors -----------------------

export interface PromptVersionResponse {
  id: UUID;
  meeting_type_id: UUID;
  version: number;
  text: string;
  is_active: boolean;
  created_by: UUID;
  created_at: ISODateTime;
}

export interface PromptVersionCreate {
  meeting_type_id: UUID;
  text: string;
  activate?: boolean;
}

/** Backend response — NEVER carries `value`. The UI must not depend on it. */
export interface ApiKeyResponse {
  id: UUID;
  provider: ApiKeyProvider;
  updated_at: ISODateTime;
}

export interface ApiKeyUpsert {
  provider: ApiKeyProvider;
  value: string;
}

export interface SystemSettingResponse {
  id: UUID;
  key: string;
  value: Record<string, unknown>;
  updated_at: ISODateTime;
}

export interface SystemSettingUpsert {
  key: string;
  value: Record<string, unknown>;
}

export interface ErrorListItem {
  id: UUID;
  /**
   * Sequential «№ ошибки» — global ROW_NUMBER over all status='error'
   * pipeline events ordered by created_at ASC. Computed server-side via a
   * window function (PipelineEvent has no autoincrement column) and stable
   * across paginated requests. Independent of UUID `id`.
   */
  error_no: number;
  meeting_id: UUID;
  /**
   * Sequential «№ встречи» from `meetings.meeting_no` (T1, FR-26). JOINed
   * in by the errors router so the registry renders «№16» as a link to
   * the meeting card instead of a UUID prefix.
   */
  meeting_no: number;
  analysis_version_id: UUID | null;
  phase: number;
  step: string;
  provider: string | null;
  message: string | null;
  attempt: number;
  created_at: ISODateTime;
}

// ----- Custom field schemas ---------------------------------------------

export interface CustomFieldSchemaResponse {
  id: UUID;
  entity_type: CustomFieldEntityType;
  key: string;
  label: string;
  field_type: CustomFieldType;
  options: Record<string, unknown> | null;
  is_required: boolean;
  is_filterable: boolean;
  order: number;
  created_at: ISODateTime;
}

export interface CustomFieldSchemaCreate {
  entity_type: CustomFieldEntityType;
  key: string;
  label: string;
  field_type: CustomFieldType;
  options?: Record<string, unknown> | null;
  is_required?: boolean;
  is_filterable?: boolean;
  order?: number;
}

export interface CustomFieldSchemaUpdate {
  label?: string;
  options?: Record<string, unknown> | null;
  is_required?: boolean;
  is_filterable?: boolean;
  order?: number;
}

// ----- User admin --------------------------------------------------------

/**
 * One folder a user is a member of, as embedded inside
 * `UserListItem.folders[]` (folder-membership v2, see
 * `docs/api/folder_membership_v2.contract.md` §1.4). NOT the same shape
 * as `FolderMember` — this is denormalised for the «Юзеры» registry to
 * avoid a per-row fan-out fetch of folder names.
 *
 * Variant A (2026-05-22): no `role` field — composition is flat. Sort
 * is BE-determined: `is_default DESC, LOWER(name) ASC`. The FE renders
 * the array as-is.
 */
export interface UserFolderMembership {
  id: UUID;
  name: string;
  /** Mirrors `folders.is_default` (system bootstrap flag, not per-user). */
  is_default: boolean;
  joined_at: ISODateTime;
}

/**
 * A folder an *open invite* will grant the future user on accept
 * (TM-1ac64335 follow-up). Only `id` + `name` — the invite has no
 * membership yet, so there is no `is_default` / `joined_at`. BE-sorted by
 * `LOWER(name)`; the FE renders as-is on the pending-invite row.
 */
export interface InviteFolderSummary {
  id: UUID;
  name: string;
}

/**
 * Extended (v2) shape of one row in `GET /api/users`
 * (`docs/api/folder_membership_v2.contract.md` §1.3). The legacy fields
 * stay populated; v2 adds the folder-membership rollup + `last_login_at`.
 * Invite rows always carry `folders=[]`, `folders_count=0`,
 * `default_folder_id=null`, `last_login_at=null`.
 */
export interface UserListItem {
  id: UUID;
  email: string;
  role: Role;
  employee_id: UUID | null;
  is_active: boolean;
  created_at: ISODateTime;
  // T4: pending invites are merged into ``GET /api/users``. ``status``
  // discriminates real users from synthetic invite rows; ``invite_id`` is
  // the lookup key the FE uses to recover a copy-paste link via
  // ``GET /api/invites/{id}/link`` (token is never exposed in the bulk
  // list for security). ``employee_display`` is the linked
  // Employee.full_name resolved server-side.
  status?: "active" | "invited";
  employee_display?: string | null;
  invite_id?: UUID | null;
  invite_expires_at?: ISODateTime | null;

  // ---- folder-membership v2 additions (§1.3) ----
  /** Sorted by BE (Variant A): is_default DESC, LOWER(name) ASC. */
  folders: UserFolderMembership[];
  folders_count: number;
  default_folder_id: UUID | null;
  last_login_at: ISODateTime | null;
  /**
   * Folders a pending invite will grant on accept (id + name). Populated
   * only for `status === "invited"` rows; active rows carry `[]` (their
   * real memberships live in `folders`). Optional so pre-existing mocks
   * that predate the field stay type-compatible.
   */
  invite_folders?: InviteFolderSummary[];
}

/**
 * POST /api/users/{user_id}/folders body (contract §2.1, Variant A).
 * The `role` field was retired together with migration 0109.
 */
export interface UserFolderMembershipCreate {
  folder_id: UUID;
}

/**
 * Response shape for POST §2.1 (Variant A — no per-folder role).
 */
export interface UserFolderMembershipResponse {
  user_id: UUID;
  folder_id: UUID;
  is_default: boolean;
  joined_at: ISODateTime;
}

export interface InviteLinkResponse {
  link: string;
}

/** Ответ POST /api/users/{id}/password-reset — сама ссылка приходит отдельно. */
export interface PasswordResetResponse {
  id: UUID;
  expires_at: string;
}

/** Ответ GET /api/users/{id}/password-reset/{reset_id}/link. */
export interface PasswordResetLinkResponse {
  link: string;
  expires_at: string;
}

export interface UserUpdate {
  is_active?: boolean | null;
  role?: Role | null;
}

// ----- Invites -----------------------------------------------------------

export interface InviteResponse {
  id: UUID;
  email: string;
  token: string;
  employee_id?: UUID | null;
  role: Role;
  expires_at: ISODateTime;
  accepted_at?: ISODateTime | null;
  created_at?: ISODateTime;
  /**
   * Folders the invited user will be granted access to on accept
   * (one-step invite, TM-1ac64335). Optional so pre-existing mocks and
   * the older single-field create path stay type-compatible.
   */
  folder_ids?: UUID[];
}

/**
 * Inline employee payload for the one-step invite (TM-1ac64335). Sent as
 * `employee` when the operator creates a brand-new employee in the same
 * dialog instead of linking an existing one (`employee_id`). The backend
 * requires exactly one of `employee_id` | `employee`.
 */
export interface InviteEmployeeInline {
  full_name: string;
  position?: string | null;
  department?: string | null;
}

export interface InviteCreate {
  email: string;
  role: Role;
  /** Link an existing employee. Mutually exclusive with `employee`. */
  employee_id?: UUID | null;
  /** Create a new employee inline. Mutually exclusive with `employee_id`. */
  employee?: InviteEmployeeInline | null;
  /** At least one folder — access is granted automatically on accept. */
  folder_ids: UUID[];
}

// ----- Phase 6: Matching --------------------------------------------------

/**
 * Status of a single matching candidate (speaker → employee, or
 * raw-client-name → client).
 *
 *  - `auto`      — backend matched above the auto-threshold (≥ 0.85 by
 *                  default; see admin/settings · `matching.auto_threshold`).
 *  - `pending`   — multiple plausible candidates or score below the auto
 *                  threshold; needs operator review.
 *  - `manual`    — operator picked or confirmed a binding.
 *  - `skipped`   — too little data / below the floor threshold (< 0.30).
 */
export type MatchingStatus = "auto" | "pending" | "manual" | "skipped";

export type MatchingEntityType = "client" | "employee";

export interface MatchingSuggestion {
  /** Entity id (client.id / employee.id). */
  entity_id: UUID;
  /** Display name (Client.name or Employee.full_name) — pre-resolved server-side. */
  display_name: string;
  /** Float in [0, 1]. */
  confidence: number;
  /** Optional position/department for employees, ИНН for clients. */
  subtitle?: string | null;
  /**
   * Per-suggestion scorer signals (TaskMe 8760a3e1 — «А из диалога какой?»).
   * Each entry is a short code emitted by the scorer (`exact_alias`,
   * `legal_form_match`, `fuzzy_name_0.92` etc.) that the UI translates
   * via the `matching.reasonCodes.*` i18n dictionary so the operator
   * never sees the raw code. Optional because legacy responses (and
   * the speakers scoreboard before this change) don't carry them yet.
   */
  reason_codes?: string[];
}

export interface MatchingCandidateClient {
  /** Speaker-candidate id (matching_candidate.id on the backend). */
  id: UUID;
  entity_type: "client";
  /** Raw value from the upload form (e.g. "ООО Ромашка"). */
  source_value: string;
  status: MatchingStatus;
  /** Currently bound entity (null when status === "pending" or "skipped"). */
  bound_entity_id: UUID | null;
  bound_display_name: string | null;
  /** Whether the binding has been explicitly confirmed by an operator. */
  confirmed: boolean;
  /** Top-N suggestions ranked by confidence DESC. */
  suggestions: MatchingSuggestion[];
}

export interface MatchingCandidateSpeaker {
  id: UUID;
  entity_type: "employee";
  /** Diarization label ("A", "B", "C", …). */
  speaker_label: string;
  /** Pre-aggregated stats from the transcript. */
  utterance_count: number;
  total_ms: number;
  status: MatchingStatus;
  bound_entity_id: UUID | null;
  bound_display_name: string | null;
  confirmed: boolean;
  suggestions: MatchingSuggestion[];
}

export type MatchingCandidate =
  | MatchingCandidateClient
  | MatchingCandidateSpeaker;

export interface MatchingResponse {
  meeting_id: UUID;
  /** Single client row (one-client-per-meeting per task spec). */
  client: MatchingCandidateClient | null;
  /** Speaker rows, ordered by `speaker_label`. */
  speakers: MatchingCandidateSpeaker[];
  /**
   * Companies the heuristic extractor noticed in the transcript +
   * consensus artifacts that do NOT correspond to the form-picked
   * client. Maps to «А из диалога какой?» (TaskMe 8760a3e1). The
   * `client` field above stays the authoritative pick; this list is
   * advisory until the operator resolves one of the entries. Each
   * row is a `MatchingCandidateClient` with `status === "pending"`
   * — render below the primary client tile as a "and these are also
   * mentioned" section. Default empty array; absent on legacy
   * responses is treated as `[]`.
   */
  client_suggestions_from_text: MatchingCandidateClient[];
  /** Count of `status === "pending"` candidates (client + speakers). */
  pending_count: number;
  /** Confidence band edges so the UI can render the threshold panel. */
  thresholds: {
    auto: number;
    review: number;
  };
}

export interface MatchingResolvePayload {
  /** Pick an existing entity by id. */
  entity_id: UUID;
  /** Optional explicit confirmation toggle. Defaults to true on the backend. */
  confirmed?: boolean;
}

export interface MatchingSkipPayload {
  /** Marks the candidate as deliberately skipped. */
  skip: true;
}

// ----- Matching v2 (docs/api/matching-v2.contract.md) --------------------
//
// Shape returned by `GET /api/meetings/:id/matching` ONLY when the backend
// flips `FEATURE_MATCHING_V2=true`. The discriminator is `transcript_revision`
// (absent on v1 responses); the FE chooses between the legacy `MatchingTab`
// and the new inline UX on that field alone.

export type MatchingConfidenceBand =
  | "high"
  | "medium_unique"
  | "medium_ambiguous"
  | "low";

/**
 * The five mention-types §1.2.1 of the contract. The UI labels them via
 * `meetings.card.matchingV2.evidence.<type>` so the operator sees Russian
 * prose instead of the raw enum.
 */
export type MentionType =
  | "self_intro"
  | "address"
  | "reference"
  | "role_marker"
  | "quotation";

export interface MatchingV2Citation {
  mention_id: UUID;
  mention_type: MentionType;
  quote: string;
  start_ms: number;
  end_ms: number;
  weight: number;
}

export interface MatchingV2EvidenceBreakdown {
  [component: string]: number;
}

export interface MatchingV2Evidence {
  breakdown: MatchingV2EvidenceBreakdown;
  citations: MatchingV2Citation[];
}

export interface MatchingV2LLMDecision {
  provider: string;
  model: string;
  employee_id: UUID | null;
  confidence_self: number;
  reasoning: string;
  prompt_hash: string;
  cached: boolean;
  cost_usd?: string;
}

export interface MatchingV2Candidate {
  id: UUID;
  suggested_employee_id: UUID;
  employee_full_name: string;
  employee_in_participants: boolean;
  confidence: number;
  confidence_band: MatchingConfidenceBand;
  status: "pending" | "resolved" | "skipped" | "conflict";
  evidence: MatchingV2Evidence;
  llm_decision: MatchingV2LLMDecision | null;
}

export interface MatchingV2BindingActor {
  id: UUID;
  name: string;
}

export interface MatchingV2Binding {
  id: UUID;
  employee_id: UUID;
  employee_full_name: string;
  employee_is_active: boolean;
  source:
    | "auto"
    | "operator"
    | "manual"
    | "operator_after_rebuild"
    | "rebuild_preserved"
    | "llm_fallback";
  status: "active" | "pending_confirm" | "superseded" | "invalidated";
  bound_at: ISODateTime | null;
  bound_by: MatchingV2BindingActor | null;
  transcript_revision_at_bind: number;
  pending_confirm_until: ISODateTime | null;
}

export interface MatchingV2RoleHint {
  value: "decision_maker" | "proposer" | "challenger" | "neutral";
  confidence: number;
  triggers: string[];
}

export interface MatchingV2Speaker {
  label: string;
  raw_label_aliases?: string[];
  total_words: number;
  duration_ms: number;
  duration_ratio: number;
  is_noise: boolean;
  requires_attention: boolean;
  role_hint: MatchingV2RoleHint | null;
  binding: MatchingV2Binding | null;
  candidates: MatchingV2Candidate[];
}

export interface MatchingV2UnmatchedMention {
  surface_text: string;
  mention_count: number;
  first_at_ms: number;
  suggestion: {
    employee_id: UUID;
    employee_full_name: string;
    in_registry: boolean;
  } | null;
  action_hint: "add_to_participants" | "mark_absent" | null;
}

export type MatchingV2InconsistencyType =
  | "participant_silent"
  | "mention_not_in_participants"
  | "double_self_intro_conflict";

export interface MatchingV2Inconsistency {
  type: MatchingV2InconsistencyType;
  employee_id?: UUID;
  employee_full_name?: string;
  mention_count?: number;
  speaker_label?: string;
  candidates?: UUID[];
  action_hint?: "add_to_participants" | "mark_absent" | null;
}

export interface MatchingV2Response {
  meeting_id: UUID;
  /**
   * Discriminator vs. v1. v1 responses do not carry this field — the FE
   * uses `"transcript_revision" in response` to decide which UI to render.
   */
  transcript_revision: number;
  feature_flags: {
    matching_v2: boolean;
    llm_fallback: boolean;
  };
  speakers: MatchingV2Speaker[];
  unmatched_mentions: MatchingV2UnmatchedMention[];
  inconsistencies: MatchingV2Inconsistency[];
  preserved_bindings_count: number;
  rate_limits: {
    rebuild_available_at: ISODateTime;
    rebuild_seconds_remaining: number;
  };
}

export interface MatchingV2ResolvePayload {
  speaker_label: string;
  employee_id: UUID;
  expected_revision: number;
}

export interface MatchingV2BulkResolvePayload {
  expected_revision: number;
}

export interface MatchingV2BulkResolveResponse {
  applied_count: number;
  applied_labels: string[];
  pending_until: ISODateTime;
  snapshot: MatchingV2Response;
}

export interface MatchingV2RebindPayload {
  speaker_label: string;
  employee_id: UUID;
  scope: "all" | "this_segment";
  expected_revision: number;
}

export interface MatchingV2UnbindPayload {
  speaker_label: string;
  expected_revision: number;
}

/** Discriminator helper — true when the API returned the v2 shape. */
export function isMatchingV2(
  resp: MatchingResponse | MatchingV2Response | null | undefined,
): resp is MatchingV2Response {
  return (
    resp !== null &&
    resp !== undefined &&
    typeof (resp as MatchingV2Response).transcript_revision === "number" &&
    Array.isArray((resp as MatchingV2Response).speakers) &&
    (resp as MatchingV2Response).speakers.length >= 0 &&
    "feature_flags" in (resp as MatchingV2Response)
  );
}

// ---------------------------------------------------------------------------
// Chunked upload (ADR-020, TM-a171ece6)
// ---------------------------------------------------------------------------

export interface UploadInitRequest {
  original_filename: string;
  expected_size: number;
  expected_sha256: string;
  folder_id?: UUID | null;
}

export interface UploadInitResponse {
  upload_id: UUID;
  chunk_size: number;
  expires_at: string;
}

export interface UploadStateResponse {
  id: UUID;
  status: "init" | "uploading" | "complete" | "expired";
  expected_size: number;
  received_offsets: number[];
  chunk_size: number;
  expires_at: string;
}

export interface UploadCompleteRequest {
  meeting_type_id: UUID;
  client_id: UUID;
  participants?: UUID[];
  folder_id?: UUID | null;
  force_new?: boolean;
}

// ---------------------------------------------------------------------------
// Manager Analysis v2 (ADR-019, TM-5743cb35)
// ---------------------------------------------------------------------------

export type ManagerAnalysisStatus = "pending" | "running" | "done" | "error";

export interface AnalysisTemplateResponse {
  id: UUID;
  title: string;
  prompt_text: string;
  is_active: boolean;
  created_by: UUID | null;
  created_at: string;
  updated_at: string;
}

export interface AnalysisTemplateUpsert {
  title: string;
  prompt_text: string;
  is_active: boolean;
}

export interface ManagerAnalysisCreatePayload {
  meeting_ids: UUID[];
  query_text?: string | null;
  template_id?: UUID | null;
}

export interface ManagerAnalysisSummary {
  id: UUID;
  employee_id: UUID;
  status: ManagerAnalysisStatus;
  query_text: string;
  template_id: UUID | null;
  result_preview: string | null;
  error_message: string | null;
  created_by: UUID;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface ManagerAnalysisResponse {
  id: UUID;
  employee_id: UUID;
  meeting_ids: UUID[];
  query_text: string;
  template_id: UUID | null;
  status: ManagerAnalysisStatus;
  result_text: string | null;
  llm_provider: string | null;
  llm_model: string | null;
  error_message: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  attempt: number;
  tmux_window_name: string | null;
  created_by: UUID;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

// ---------------------------------------------------------------------------
// List Analysis (TM-4c1fb82b) — «анализ по списку встреч»
//
// Multi-meeting analysis triggered from three surfaces: the meetings registry
// (mode="free"), a client card (mode="client") and an employee card
// (mode="manager"). Multi-model + consensus: 1..3 `llm_models`; a
// `consensus_model` is required when more than one model is chosen and MUST be
// null when exactly one is chosen. Feature-gated behind the backend router:
// a 404 from `GET /api/list_analyses` means the feature is off/undeployed.
// ---------------------------------------------------------------------------

export type ListAnalysisMode = "manager" | "client" | "free";

export type ListAnalysisStatus = "pending" | "running" | "done" | "error";

export interface ListAnalysisCreate {
  mode: ListAnalysisMode;
  /**
   * Optional human-readable title for the analyses registry (<=255 chars).
   * When omitted/empty the server fills it: template title (if `template_id`)
   * or the first 60 chars of `query_text`.
   */
  title?: string | null;
  /** Required when `mode="manager"`, otherwise null. */
  employee_id: UUID | null;
  /** Required when `mode="client"`, otherwise null. */
  client_id: UUID | null;
  /** 1..20 meeting ids, no duplicates. */
  meeting_ids: UUID[];
  /** `query_text` OR `template_id` must be provided. */
  query_text: string | null;
  template_id: UUID | null;
  /** 1..3 models, no duplicates. */
  llm_models: LLMModelName[];
  /** Required when `llm_models.length > 1`; MUST be null when length === 1. */
  consensus_model: LLMModelName | null;
}

export interface ListAnalysisSummary {
  id: UUID;
  mode: ListAnalysisMode;
  /** Registry title; may be null for rows created before the title column. */
  title: string | null;
  employee_id: UUID | null;
  client_id: UUID | null;
  status: ListAnalysisStatus;
  query_text: string;
  template_id: UUID | null;
  llm_models: LLMModelName[];
  consensus_model: LLMModelName | null;
  /** First 300 chars of `result_text` once `status="done"`. */
  result_preview: string | null;
  error_message: string | null;
  meetings_count: number;
  created_by: UUID;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** Display names resolved server-side (batch, no N+1). */
  employee_display: string | null;
  client_display: string | null;
  author_display: string | null;
}

export interface ListAnalysisResponse extends ListAnalysisSummary {
  meeting_ids: UUID[];
  result_text: string | null;
  /** Model of the final text (or the consensus model). */
  llm_model_used: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}
